import axios, { AxiosError } from 'axios';
import { useAuthContext } from 'contexts/AuthContext';
import { useQuery } from 'react-query';
import { IResTheme } from 'types/api';
import { IAppTheme } from 'types/theme';
import { convertTheme } from 'utils/convertTheme';
import { HTTP_STATUS_CODE } from './api';
import { API_ROUTES } from './apiRoutes';
import { useAppConfig } from './appConfig';
import QueryKeys from './queryKeys';
import { staticBaseURL } from './setup';

const getTheme = async (
	projectId: string | null,
	styleId?: string | null
): Promise<IAppTheme> => {
	if (!projectId) {
		return { light: {}, dark: {} };
	}

	// try twice to get the theme
	for (let i = 0; i < 2; i += 1) {
		try {
			// eslint-disable-next-line no-await-in-loop
			const themeRes = await axios.get<IResTheme>(
				`${staticBaseURL()}${API_ROUTES.theme(projectId, styleId || 'sso-suite-default')}`,
				{ allowAbsoluteUrls: true }
			);
			const convertedTheme = convertTheme(themeRes.data);
			return convertedTheme;
		} catch (err) {
			/* istanbul ignore next: should fail the query */
			if ((err as AxiosError).status !== HTTP_STATUS_CODE.notFound) {
				throw err;
			}
		}
	}

	return { light: {}, dark: {} };
};

const useStyledTheme = (enabled: boolean) => {
	const { authRes } = useAuthContext();
	const { data: appConfigData, isError: appConfigError } = useAppConfig();

	return useQuery({
		queryKey: [QueryKeys.theme, appConfigData?.styleId],
		queryFn: () => getTheme(authRes.projectId, appConfigData?.styleId),
		staleTime: Infinity,
		enabled: enabled && (!!appConfigData || appConfigError),
		refetchOnWindowFocus: false,
		keepPreviousData: true,
		retry: false
	});
};

export { useStyledTheme };
